import momentjs = require("moment");
import React = require("react");
import {Moment} from "moment";
import DatePicker from "react-datepicker";
import {Buttons} from "../../buttons/Buttons";
import {Types} from "../../../types/Types";
import {I18n} from "../../Utils/i18n";
import {TabSelector} from "../../TabSelector";
import {Granularity, GranularityType} from "../Toolbar";
import {ToolbarContextConsumer, ToolbarContextInterface} from "../ToolbarContext";
import {Timeline} from "./Timeline";
import "./TimelineNavigator.scss";
import {Tooltip} from "@vebox/etui-core";

declare var LOCALE: string;

interface TimelineNavigatorProps {
    key: any;
    width: number;
    enabled: boolean;
}

export interface TimelinePeriods {
    currentMaxExtent: Types.Period;
    currentExtent: Types.Period;
    selectedPeriod: Types.Period;
}

export class TimelineNavigator extends React.Component<TimelineNavigatorProps, {}> {

    /**
     * Given the current extent of the timeline and a new granularity,
     * adjust the extent agree with the current selected period and available limits of the new granularity:
     */
    public static validateTimeline(
        currentExtent: Types.Period,
        granularity: Granularity,
        currentSelectedPeriod: Types.Period,
        maxExtent: Types.Period,
        currentMaxExtent: Types.Period): TimelinePeriods {
        const duration: number = currentExtent.end.diff(currentExtent.start, "milliseconds");
        const currentSelectedPeriodDuration: number
            = currentSelectedPeriod.end.diff(currentSelectedPeriod.start, "milliseconds");
        let newExtent: Types.Period = TimelineNavigator.adjustPeriod(currentExtent, granularity);
        let newSelectedPeriod = TimelineNavigator.adjustPeriod(currentSelectedPeriod, granularity);
        if (duration > granularity.rangeLimits.max.asMilliseconds()) {
            // current extent non compatible with current granularity reduce it
            if (newExtent.start.diff(currentSelectedPeriod.start, "millisecond") === 0) {
                newExtent.end = currentSelectedPeriod.start.clone();
                newExtent.end.add(
                    Math.min(granularity.rangeLimits.max.asMilliseconds(), currentSelectedPeriodDuration),
                );
            } else if (newExtent.end.diff(currentSelectedPeriod.end, "millisecond") === 0) {
                newExtent.start = newExtent.end.clone();
                newExtent.start.subtract(
                    Math.min(granularity.rangeLimits.max.asMilliseconds(), currentSelectedPeriodDuration),
                );
            } else {
                newExtent = currentSelectedPeriod;
            }
            newExtent = TimelineNavigator.adjustPeriod(newExtent, granularity);
            newExtent = TimelineNavigator.adjustPeriod(newExtent, granularity);
            newSelectedPeriod = newExtent;
        }
        return {
            currentMaxExtent,
            currentExtent: newExtent,
            selectedPeriod: newSelectedPeriod,
        };
    }

    /**
     * Given a period truncates the start and rounds the end agree with the given granularity
     */
    public static adjustPeriod(newPeriod: Types.Period, newGranularity: Granularity): Types.Period {
        if (newGranularity === undefined) {
            return newPeriod;
        }
        const newStartPeriodTruncated = TimelineNavigator.truncateMoment(newPeriod.start.clone(), newGranularity.key);
        const newStartPeriodRounded = TimelineNavigator.roundMoment(newPeriod.start.clone(), newGranularity.key, true);

        const startDiffFromTruncated = newPeriod.start.diff(newStartPeriodTruncated);
        const startDiffFromRounded = newStartPeriodRounded.diff(newPeriod.start);

        const newEndPeriodTruncated = TimelineNavigator.truncateMoment(newPeriod.end.clone(), newGranularity.key);
        const newEndPeriodRounded = TimelineNavigator.roundMoment(newPeriod.end.clone(), newGranularity.key, true);

        const endDiffFromTruncated = newPeriod.end.diff(newEndPeriodTruncated);
        const endDiffFromRounded = newEndPeriodRounded.diff(newPeriod.end);

        const periodAdjusted: Types.Period = {
            start: ((startDiffFromTruncated === 0 || startDiffFromTruncated <= startDiffFromRounded) ?
                newStartPeriodTruncated : newStartPeriodRounded),
            end: ((endDiffFromTruncated === 0 || endDiffFromTruncated <= endDiffFromRounded) ?
                newEndPeriodTruncated : newEndPeriodRounded),
        };
        return periodAdjusted;
    }

    private static logEnabled: boolean = false;

    /*
    * Truncate the given moment agree with the given granularity
    */
    private static truncateMoment(moment: Moment, granularity: GranularityType) {
        switch (granularity) {
            case GranularityType.HOUR: {
                moment = moment.startOf("hour");
                break;
            }
            case GranularityType.DAY: {
                moment = moment.startOf("day");
                break;
            }
            case GranularityType.WEEK: {
                moment = moment.startOf("isoWeek");
                break;
            }
            case GranularityType.MONTH: {
                moment = moment.startOf("month");
                break;
            }
            case GranularityType.QUARTER: {
                moment = moment.startOf("quarter");
                break;
            }
            case GranularityType.YEAR: {
                moment = moment.startOf("year");
                break;
            }
        }
        return moment;
    }

    /**
     * Round up the given moment considering the given granularity.
     * If the given moment is already compliant the given granularity returns it without changes
     * if the nextStep param is false or undefined other with round up it
     */
    private static roundMoment(moment: Moment, granularity: GranularityType, nextStep?) {
        let roundedMoment = TimelineNavigator.truncateMoment(moment, granularity);
        if (roundedMoment.toDate().getTime() === moment.toDate().getTime() && !nextStep) {
            return roundedMoment;
        }
        switch (granularity) {
            case GranularityType.HOUR: {
                roundedMoment = roundedMoment.add(1, "hour");
                break;
            }
            case GranularityType.DAY: {
                roundedMoment = roundedMoment.add(1, "day");
                break;
            }
            case GranularityType.WEEK: {
                roundedMoment = roundedMoment.add(1, "week");
                break;
            }
            case GranularityType.MONTH: {
                roundedMoment = roundedMoment.add(1, "month");
                break;
            }
            case GranularityType.QUARTER: {
                roundedMoment = roundedMoment.add(1, "quarter");
                break;
            }
            case GranularityType.YEAR: {
                roundedMoment = roundedMoment.add(1, "year");
                break;
            }
        }
        return roundedMoment;
    }

    private datePickerStart;
    private datePickerEnd;

    constructor(props: TimelineNavigatorProps) {
        super(props);
    }

    public render() {
        const content = (
            <div className={"timeline-navigator__content"}>
                <ToolbarContextConsumer>
                    {(context) =>
                        <React.Fragment>
                            <div className={"timeline-navigator__content__options"}>
                                <TabSelector
                                    buttons={context.granularities.map((g) => {
                                        return {
                                            key: g.key,
                                            text: g.name,
                                            enabled: this.props.enabled && g.available,
                                        };
                                    })}
                                    onSelect={this.onChangeGranularity(context)}
                                    activeButtonKey={context.granularitySelected}
                                />
                            </div>
                            <div className={"timeline-navigator__content__timeline"}>
                                <Tooltip
                                    message={"- " + context.getPrettyExtentDuration()}
                                    styles={{
                                        root: {color: "black", background: "whitesmoke"},
                                        div: {color: "black", background: "whitesmoke"}
                                    }}
                                >
                                    <div className={"move-extent-element"}>
                                        <Buttons.ButtonWithIcon
                                            key={"navButtonLeft"}
                                            code={"prev"}
                                            icon={"/vebox/images/buttons/icon-previous.png"}
                                            height="20px"
                                            width="20px"
                                            loading={false}
                                            variant="secondary"
                                            onClick={context.handleMoveExtent}
                                            selected={false}
                                            disabled={!this.props.enabled || !context.canMoveExtentBack}
                                        />
                                    </div>
                                </Tooltip>
                                <div className="date-picker-element--left">
                                    <DatePicker
                                        ref={(datePickerStart) => this.datePickerStart = datePickerStart}
                                        selected={context.timelineExtent.start.toDate()}
                                        minDate={context.timelineMaxExtent.start.toDate()}
                                        maxDate={context.timelineExtent.end.clone().subtract(1, "day").toDate()}
                                        onChange={this.onChangeStart(context)}
                                        dateFormat={I18n.getLocaleDateFormat(LOCALE)}
                                        //locale={LOCALE}
                                        onKeyDown={this.closeDatePickerStartPopper}
                                        disabledKeyboardNavigation={true}
                                        showMonthDropdown={true}
                                        showYearDropdown={true}
                                        readOnly={!this.props.enabled}
                                    />
                                </div>
                                <Timeline
                                    key={1}
                                    enabled={this.props.enabled}
                                    totalPeriod={context.timelineExtent}
                                    selectedPeriod={context.timelineSelectedPeriod}
                                    size={{
                                        width: (this.props.width > 113 * 2
                                            ? this.props.width - (113 * 2) - 20
                                            : this.props.width),
                                        height: 30,
                                    }}
                                    margin={{
                                        left: this.props.width * 0.2 / 2,
                                        top: 30,
                                        bottom: 0,
                                        right: this.props.width * 0.2 / 2,
                                    }}
                                    dateTimeFormat={I18n.getLocaleMomentFormat(LOCALE)}
                                    handleBrush={context.handleTimelineSelectedPeriodChange}
                                    handleBrushEnd={context.handleBrushEnd}
                                    selectedGranularity={context.getGranularitySelected()}
                                />
                                <div className="date-picker-element--right">
                                    <DatePicker
                                        ref={(datePickerEnd) => this.datePickerEnd = datePickerEnd}
                                        selected={context.timelineExtent.end.toDate()}
                                        minDate={context.timelineExtent.start.clone().add(1, "day").toDate()}
                                        maxDate={context.timelineMaxExtent.end.toDate()}
                                        onChange={this.onChangeEnd(context)}
                                        onKeyDown={this.closeDatePickerEndPopper}
                                        dateFormat={I18n.getLocaleDateFormat(LOCALE)}
                                        disabledKeyboardNavigation={true}
                                        popperPlacement={"bottom-end"}
                                        showMonthDropdown={true}
                                        showYearDropdown={true}
                                        readOnly={!this.props.enabled}
                                    />
                                </div>
                                <Tooltip
                                    message={"+ " + context.getPrettyExtentDuration()}
                                    styles={{
                                        root: {color: "black", background: "whitesmoke"},
                                        div: {color: "black", background: "whitesmoke"}
                                    }}
                                >
                                    <div className={"move-extent-element"}>
                                        <Buttons.ButtonWithIcon
                                            key={"navButtonRight"}
                                            code={"next"}
                                            icon={"/vebox/images/buttons/icon-next.png"}
                                            height="20px"
                                            width="20px"
                                            loading={false}
                                            variant="secondary"
                                            onClick={context.handleMoveExtent}
                                            selected={false}
                                            disabled={!this.props.enabled || !context.canMoveExtentForward}
                                        />
                                    </div>
                                </Tooltip>
                            </div>
                        </React.Fragment>
                    }
                </ToolbarContextConsumer>
            </div>
        );
        return (
            <div className={"timeline-navigator__container"}>
                {content}
            </div>
        );
    }

    /**
     * Given a toolbar context return a function that validate the context agree with a given new granularity
     */
    private onChangeGranularity(context: ToolbarContextInterface) {
        return (newGranularity: number) => {
            const granularity = context.granularities.filter((g) => g.key === newGranularity).pop();
            if (granularity.available) {
                const timelinePeriods = TimelineNavigator.validateTimeline(
                    context.timelineExtent,
                    granularity,
                    context.timelineSelectedPeriod,
                    context.timelineMaxExtent,
                    context.timelineMaxExtent);
                context.handleGranularitySelectedChange(
                    newGranularity,
                    timelinePeriods.currentExtent,
                    timelinePeriods.selectedPeriod,
                    timelinePeriods.currentMaxExtent,
                );
            }
        };
    }

    private onChangeStart = (context: ToolbarContextInterface) => {
        return (e) => {
            if (this.checkDatePickerValue(e, context, true)) {
                context.handleTimelineExtentStartChange(e);
            } else {
                context.handleTimelineExtentStartChange(context.timelineExtent.start.toDate());
            }
        };
    }

    private onChangeEnd = (context: ToolbarContextInterface) => {
        return (e) => {
            if (this.checkDatePickerValue(e, context, false)) {
                context.handleTimelineExtentEndChange(e);
            } else {
                context.handleTimelineExtentEndChange(context.timelineExtent.end.toDate());
            }
        };
    }
    private closeDatePickerStartPopper = (key: any) => {
        this.datePickerStart.setState(
            {open: false});
    }

    private closeDatePickerEndPopper = (key: any) => {
        this.datePickerEnd.setState(
            {open: false});
    }

    private checkDatePickerValue = (
        date: Date,
        context: ToolbarContextInterface,
        isStartDatePicker: boolean): boolean => {
        if (date === null) {
            return false;
        }
        if (isStartDatePicker && (
            momentjs(date.getTime()).isAfter(context.timelineExtent.end) ||
            date.getTime() === context.timelineExtent.end.valueOf() ||
            date.getTime() < context.timelineMaxExtent.start.valueOf())
        ) {
            return false;
        }
        if (!isStartDatePicker &&
            (momentjs(date.getTime()).isBefore(context.timelineExtent.start) ||
                date.getTime() === context.timelineExtent.start.valueOf() ||
                date.getTime() > context.timelineMaxExtent.end.valueOf())) {
            return false;
        }
        return true;
    }

    private log(message: any): void {
        if (TimelineNavigator.logEnabled) {
            console.log(message);
        }
    }
}
